import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import { useBffClient } from 'src/app/hooks/data/useBffClient';

import { CACHE_KEY_PHARMACY_HUB_ENTRY_ID, CACHE_KEY_PHARMACY_HUB_SHORT_CODE } from './_keys';

type TriggerPharmacyDemo = {
  phone: string;
  patient_name?: string | null;
  doctor_name?: string | null;
  variant?: string | null;
  pharmacy_name?: string | null;
  pharmacy_address?: string | null;
};

export const useTriggerPharmacyHubDemoMutation = (): UseMutationResult<string, AxiosError, TriggerPharmacyDemo> => {
  const client = useBffClient();

  return useMutation((triggerRequest: TriggerPharmacyDemo) =>
    client
      .post(`/partners/trigger-pharmacy-hub-demo`, triggerRequest, {
        headers: { 'Content-Type': 'application/json' },
      })
      .then((resp: { data: string }) => resp.data)
  );
};

type FullUrlResponse = {
  longURL: string;
};

export const useExchangeForFullUrl = (shortCode: string): UseQueryResult<FullUrlResponse, AxiosError> => {
  const client = useBffClient();

  return useQuery(
    [CACHE_KEY_PHARMACY_HUB_SHORT_CODE, shortCode],
    () => client.get(`/auth/get_full_url/${shortCode}`).then(resp => resp.data),
    {
      enabled: true,
      retry: 1,
      refetchOnWindowFocus: false,
    }
  );
};

interface PharmacyHubEntryMedication {
  ndc?: string;
  days_supply?: number;
  quantity?: number;
  dosage_form_label?: string;
  strength?: string;
  primary_name?: string;
  secondary_name?: string;
  num_refills_remaining: number;
}

interface CapsuleMedIneligibilityReason {
  ndc?: string;
  reason?: string;
}

export interface PharmacyHubEntryResponse {
  can_show_capsule?: boolean;
  capsule_ineligibility_reason?: CapsuleMedIneligibilityReason;
  is_deliverable?: boolean;
  zip_code?: string;
  latitude?: number;
  longitude?: number;
  facility_name?: string;
  medications: PharmacyHubEntryMedication[];
  patient: {
    id: string;
    first_name: string;
    last_name: string;
    birthdate: string;
  };
  provider: {
    first_name: string;
    last_name: string;
    npi: string;
    practice_name?: string;
  };
  selected_pharmacy: {
    selected_pharmacy_option: string;
    pharmacy_selected_at?: string;
    selected_pharmacy_name?: string;
    selected_pharmacy_address?: string;
    selected_pharmacy_phone?: string;
  };
  previous_pharmacy?: {
    name: string;
    address1?: string;
    address2?: string;
    city?: string;
    state?: string;
    zipcode?: string;
  };
  department?: {
    name: string;
    pkid: number;
    emr_department_id?: string;
  };
  windows?: {
    has_same_day_delivery: boolean;
    has_next_day_delivery: boolean;
  };
  additional_pharmacy_hub_pending_escript_uuids?: string[];
}

export const useGetHubPendingEscripts = (
  entryUUID: string,
  enabled = true
): UseQueryResult<PharmacyHubEntryResponse, AxiosError> => {
  const client = useBffClient();

  return useQuery(
    [CACHE_KEY_PHARMACY_HUB_ENTRY_ID, entryUUID],
    () => client.get(`/v3/pharmacy_hub/${entryUUID}`).then(resp => resp.data),
    {
      enabled: enabled,
      retry: 2,
      refetchOnWindowFocus: false,
    }
  );
};

type SelectPharmacyRequest = {
  entryUUID: string;
  isCapsuleSelected: boolean;
  pharmacyName: string;
  pharmacyAddress?: string | null;
  pharmacyPhone?: string | null;
  additional_pharmacy_hub_pending_escript_uuids?: string[];
};

export const useSelectPharmacyForPendingEscripts = (): UseMutationResult<string, AxiosError, SelectPharmacyRequest> => {
  const client = useBffClient();

  return useMutation((selectPharmacyRequest: SelectPharmacyRequest) =>
    client
      .post(
        `/v3/pharmacy_hub/${selectPharmacyRequest.entryUUID}/selection`,
        {
          selected_pharmacy_option: selectPharmacyRequest.isCapsuleSelected ? 'capsule' : 'other',
          selected_pharmacy_name: selectPharmacyRequest.pharmacyName,
          selected_pharmacy_address: selectPharmacyRequest.pharmacyAddress,
          selected_pharmacy_phone: selectPharmacyRequest.pharmacyPhone,
          additional_pharmacy_hub_pending_escript_uuids:
            selectPharmacyRequest.additional_pharmacy_hub_pending_escript_uuids || [],
        },
        {
          headers: { 'Content-Type': 'application/json' },
        }
      )
      .then((resp: { data: string }) => resp.data)
  );
};
